import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { Platform } from 'react-native';

import type { ProfileImageKind } from '@/src/features/profile/profile-service';

export const usesProfileImageCropper = Platform.OS === 'web';
export const PROFILE_COVER_CROP_WIDTH = 592;
export const PROFILE_COVER_CROP_HEIGHT = 164;
export const PROFILE_COVER_CROP_ASPECT =
  PROFILE_COVER_CROP_WIDTH / PROFILE_COVER_CROP_HEIGHT;

export type ProfileImageCropRect = {
  originX: number;
  originY: number;
  width: number;
  height: number;
};

export async function pickProfileImageAsset(kind: ProfileImageKind) {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();

  if (!permission.granted) {
    throw new Error('Photo library permission is required.');
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    allowsEditing: !usesProfileImageCropper,
    aspect:
      kind === 'avatar'
        ? [1, 1]
        : [PROFILE_COVER_CROP_WIDTH, PROFILE_COVER_CROP_HEIGHT],
    mediaTypes: ['images'],
    quality: 0.9,
  });

  if (result.canceled || !result.assets[0]) {
    return null;
  }

  return result.assets[0];
}

export async function cropProfileImageAsset({
  asset,
  crop,
  kind,
}: {
  asset: ImagePicker.ImagePickerAsset;
  crop: ProfileImageCropRect;
  kind: ProfileImageKind;
}): Promise<ImagePicker.ImagePickerAsset> {
  const result = await ImageManipulator.manipulateAsync(
    asset.uri,
    [
      {
        crop: {
          height: Math.max(1, Math.round(crop.height)),
          originX: Math.max(0, Math.round(crop.originX)),
          originY: Math.max(0, Math.round(crop.originY)),
          width: Math.max(1, Math.round(crop.width)),
        },
      },
    ],
    {
      compress: kind === 'avatar' ? 0.9 : 0.88,
      format: ImageManipulator.SaveFormat.JPEG,
    },
  );

  return {
    ...asset,
    file: undefined,
    fileName: `${kind}-cropped.jpg`,
    fileSize: undefined,
    height: result.height,
    mimeType: 'image/jpeg',
    uri: result.uri,
    width: result.width,
  };
}
